// Dailies: the opt-in mail with what changed (new takes, notes for you, what waits on your sign-off). The API
// (core/api routes/dailies.ts) keeps one preference per person and workspace; this file is its client, the form
// state around it and the few pure words the settings page and the unsubscribe landing need.
import type { Api } from "../api.ts";

export type Cadence = "daily" | "weekdays" | "weekly";

export const CADENCE_LABEL: Record<Cadence, string> = {
  daily: "Every day",
  weekdays: "Weekdays",
  weekly: "Once a week",
};

/** ISO weekdays, Monday first (the API counts 1 Monday to 7 Sunday). */
export const WEEKDAYS: readonly { id: number; label: string }[] = [
  { id: 1, label: "Monday" },
  { id: 2, label: "Tuesday" },
  { id: 3, label: "Wednesday" },
  { id: 4, label: "Thursday" },
  { id: 5, label: "Friday" },
  { id: 6, label: "Saturday" },
  { id: 7, label: "Sunday" },
];

export interface Dailies {
  enabled: boolean;
  cadence: Cadence;
  hour: number;
  weekday: number | null;
  timezone: string;
  next_due_at: string | null;
  last_sent_at: string | null;
  /** `log`: this server has no mail provider, dailies only reach its log */
  delivery: "resend" | "log";
}

export interface DailiesBody {
  enabled: boolean;
  cadence?: Cadence;
  hour?: number;
  weekday?: number | null;
  timezone?: string;
}

/** What the form edits. Always complete, so it can be compared with what the server holds. */
export interface DailiesForm {
  enabled: boolean;
  cadence: Cadence;
  hour: number;
  weekday: number;
  timezone: string;
}

export function dailiesApi(api: Api, ws: string) {
  const path = `/workspaces/${ws}/dailies`;
  return {
    get: () => api.request<Dailies>("GET", path),
    set: (body: DailiesBody) => api.request<Dailies>("PUT", path, { body }),
  };
}

// ─── Time zones ──────────────────────────────────────────────────────────────────────────────────────────

/** The zone this device is in, or UTC when the browser will not say. */
export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const FALLBACK_ZONES = [
  "UTC",
  "Europe/London",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Paris",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Asia/Tokyo",
  "Australia/Sydney",
];

/** Every IANA zone the browser knows (the API accepts what the runtime knows), UTC and `extra` always among them. */
export function zoneList(extra: readonly string[] = []): string[] {
  let zones: string[] = [];
  try {
    zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
      "timeZone",
    ) ?? [...FALLBACK_ZONES];
  } catch {
    zones = [...FALLBACK_ZONES];
  }
  return [...new Set(["UTC", ...zones, ...extra.filter(Boolean)])].sort((a, b) =>
    a === "UTC" ? -1 : b === "UTC" ? 1 : a.localeCompare(b),
  );
}

/**
 * The zone the person means, in its proper spelling: a zone name ignoring case ("europe/amsterdam"), or a city
 * ("Amsterdam", "new york" → America/New_York; the first in the list when two regions share a city). Null when
 * nothing matches.
 */
export function matchZone(input: string, zones: readonly string[]): string | null {
  const t = input.trim().toLowerCase();
  if (!t) return null;
  const exact = zones.find((z) => z.toLowerCase() === t);
  if (exact) return exact;
  const city = t.replace(/[\s_]+/g, "_");
  return zones.find((z) => z.includes("/") && z.slice(z.lastIndexOf("/") + 1).toLowerCase() === city) ?? null;
}

// ─── Form ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Preserve the server's zone: the API returns UTC even without a preference row, so an unset preference
 * cannot be distinguished from a deliberately saved UTC schedule. The device zone is an explicit action.
 */
export function formFrom(d: Dailies): DailiesForm {
  return {
    enabled: d.enabled,
    cadence: d.cadence,
    hour: d.hour,
    weekday: d.weekday ?? 1,
    timezone: d.timezone,
  };
}

/** Is there something to save? While dailies stay off, nothing but the switch counts. */
export function isDirty(form: DailiesForm, d: Dailies): boolean {
  if (form.enabled !== d.enabled) return true;
  if (!form.enabled) return false;
  return (
    form.cadence !== d.cadence ||
    form.hour !== d.hour ||
    form.timezone !== d.timezone ||
    (form.cadence === "weekly" && form.weekday !== (d.weekday ?? 1))
  );
}

/** The request for the form. Off sends only the off: the schedule stays as it was. */
export function bodyFrom(form: DailiesForm): DailiesBody {
  if (!form.enabled) return { enabled: false };
  return {
    enabled: true,
    cadence: form.cadence,
    hour: form.hour,
    weekday: form.cadence === "weekly" ? form.weekday : null,
    timezone: form.timezone,
  };
}

export const hourText = (h: number): string => `${String(h).padStart(2, "0")}:00`;

/** "Tuesday 28 October, 08:00 CET": the moment of the next mail on the wall clock of `timezone`. */
export function nextSendText(iso: string, timezone: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  try {
    const day = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(t);
    const time = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZoneName: "short",
    }).format(t);
    return `${day}, ${time}`;
  } catch {
    return t.toISOString();
  }
}

export const OFF_TEXT =
  "Dailies are off. Turn them on to get one mail with what changed: new takes, notes for you, what waits on your sign-off.";

export const LOG_TEXT = "Mail isn't set up on this server yet; dailies are written to the server log.";

// ─── Unsubscribe link ────────────────────────────────────────────────────────────────────────────────────

export interface Unsubscribed {
  workspace: string;
  enabled: boolean;
}

/** The public pair behind the link in a mail. The token is the credential: no sign-in, no workspace. */
export function unsubscribeApi(api: Api) {
  const path = (token: string) => `/dailies/unsubscribe/${encodeURIComponent(token)}`;
  return {
    /** who and what; changes nothing */
    look: (token: string) => api.request<Unsubscribed>("GET", path(token)),
    stop: (token: string) => api.request<Unsubscribed>("POST", path(token)),
  };
}
